/**
 * Slim Uren-regels — bekijk en stuur (via chat) de koppelregels bij die bepalen op
 * welke interne Gripp-regel een taak/afspraak wordt geboekt. Trefwoorden → regel;
 * "overslaan" = geen uren voorstellen. Opslaan gaat naar setting `slim_uren_rules`.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Stack, Paper, Chip, Alert } from '@mui/material';
import { SectionHeader } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { MijnRegels } from '../../SlimUren/SlimUrenLeren';

export default function SlimUrenRegels() {
  const [rules, setRules] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/slim-uren/rules`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Regels laden mislukt');
      setRules(d.rules); setIsDefault(!!d.isDefault);
    } catch (e) { setError(e.message); setRules({ mapping: [], exact: {} }); }
  };
  useEffect(() => { load(); }, []);

  const persist = async (newRules) => {
    setSaving(true); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/slim-uren/rules`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rules: newRules }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Opslaan mislukt');
      setRules(d.rules); setIsDefault(false);
      setMsg('Regels opgeslagen — direct actief bij het volgende voorstel.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError(null); setMsg(null);
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next); setInput(''); setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/slim-uren/rules-chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messages: next, rules }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Aanpassen mislukt');
      setMessages((m) => [...m, { role: 'assistant', content: d.reply || 'Aangepast.' }]);
      await persist(d.rules);   // meteen bewaren
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Box>
      <SectionHeader
        title="Slim Uren-regels"
        subtitle="Welke taak/afspraak op welke interne Gripp-regel wordt geboekt. Bekijk ze en stuur ze in gewone taal bij."
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert>}

      {rules === null ? <CircularProgress size={20} /> : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569' }}>
                Koppelregels (van boven naar beneden — eerste match wint)
              </Typography>
              {isDefault && <Chip size="small" label="standaard" sx={{ height: 20, fontSize: 10 }} />}
            </Box>
            <Stack spacing={0.5}>
              {(rules.mapping || []).map((m, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: 13.5, color: '#334155', display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <span style={{ color: '#94a3b8', minWidth: 18 }}>{i + 1}.</span>
                  <b>{(m.keywords || []).join(', ')}</b>
                  <span style={{ color: '#94a3b8' }}>→</span>
                  {m.line === null
                    ? <span style={{ color: '#b45309', fontWeight: 700 }}>overslaan (geen uren)</span>
                    : <span style={{ color: '#0f766e', fontWeight: 700 }}>{m.line}</span>}
                </Typography>
              ))}
              {(rules.mapping || []).length === 0 && (
                <Typography variant="caption" color="text.secondary">Geen regels — gebruik de chat om ze op te bouwen.</Typography>
              )}
            </Stack>
            {Object.keys(rules.exact || {}).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Exacte korte namen: {Object.entries(rules.exact).map(([k, v]) => `"${k}" → ${v}`).join(' · ')}
              </Typography>
            )}
            {/* Extra lagen (okt 2026) — aanpassen gaat via de chat hieronder. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              <b>Overslaan in de agenda (geen werk):</b>{' '}
              {Array.isArray(rules.ruis) ? (rules.ruis.join(', ') || 'niets') : 'standaard (privé, reistijd, reis, reizen, onderweg, rijden, ophalen, verhuizing, huur)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              <b>Uren voor een offerte:</b>{' '}
              {rules.offerte_beleid === 'intern'
                ? 'altijd intern (oude regel, zie hieronder)'
                : 'op die offerte zelf, regel Projectbegeleiding en communicatie (als er één duidelijke open offerte van die klant rond die dag is)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              <b>{rules.offerte_beleid === 'intern' ? 'Offerte zonder opdracht' : 'Terugval zonder duidelijke offerte'}:</b>{' '}
              {rules.offerte_intern
                ? `${(rules.offerte_intern.regels || []).map((r) => `${(r.keywords || []).join('/')} → ${r.line}`).join(' · ')}${(rules.offerte_intern.regels || []).length ? ' · ' : ''}anders → ${rules.offerte_intern.standaard}`
                : 'standaard (pitch/rfp/tender → Pitch / RFP · borrel/relatie → Sales · anders → Offerte maken)'}
            </Typography>
            {(rules.aliassen || []).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                <b>Vaste aliassen:</b>{' '}
                {rules.aliassen.map((a) => `${(a.trefwoorden || []).join('/')} → ${a.naam || a.project}${a.email ? ` (alleen ${a.email})` : ''}`).join(' · ')}
              </Typography>
            )}
          </Paper>

          {messages.length > 0 && (
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              {messages.map((m, i) => (
                <Box key={i} sx={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%',
                  px: 1.5, py: 1, borderRadius: 1.5, fontSize: 13.5, whiteSpace: 'pre-wrap',
                  bgcolor: m.role === 'user' ? '#e0e7ff' : '#f1f5f9',
                }}>{m.content}</Box>
              ))}
              {(busy || saving) && <CircularProgress size={16} sx={{ alignSelf: 'flex-start', ml: 1 }} />}
            </Stack>
          )}

          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" placeholder='Bijv. "Sla ‘standup’ over" of "HNL hoort bij project 9753, alleen voor joris@fullframe.nl"'
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button variant="contained" disabled={busy || saving || !input.trim()} onClick={send}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Stuur bij</Button>
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" color="warning" disabled={saving || isDefault}
              onClick={() => persist({})} sx={{ textTransform: 'none' }}>
              Terug naar standaardregels
            </Button>
          </Box>
        </>
      )}

      {/* Persoonlijk (leerlus, okt 2026): wat Slim Uren van jóúw geschreven uren leerde — alleen je eigen regels. */}
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', mb: 1 }}>
          Mijn geleerde regels (alleen van jou)
        </Typography>
        <MijnRegels />
      </Paper>
    </Box>
  );
}
